import type {sessionType} from "@/assets/ts/types/session";
import {i18n} from "@/main";

export class Sessions {
    private getLocaleParams(): { country: string, language: string } {
        const locale = (i18n.global.locale as any).value || "de-DE";

        let [language, country] = locale.split("-");

        if (locale.split("-").length === 3) {
            language = locale.split("-").splice(0, 2).join("-");
            country = locale.split("-").splice(2, 1).join("-");
        }

        return {country, language};
    }

    create(item_number: string, filters: { [key: string]: string }) {
        const {country, language} = this.getLocaleParams();

        const item_numbers = item_number.includes(";") ? item_number.split(";") : [item_number];

        return new Promise((resolve, reject) => {
            fetch(process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language, {
                method: "PUT",
                mode: "cors",
                cache: "no-cache",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    items: item_numbers,
                    filters: filters
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data.session_id == "string") {
                        resolve(data.session_id);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    get(sessionId: string) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase(),
                {
                    method: "GET",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    getSimple(sessionId: string) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase() + "/simple",
                {
                    method: "GET",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    getVariants(sessionId: string, itemIdx: number) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase() + "/variants/" + itemIdx,
                {
                    method: "GET",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    getVariantValues(sessionId: string, itemIdx: number, variantIdx: string, variantIdxAddon: string = "") {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase() + "/variants/" + itemIdx + "/" + encodeURIComponent(variantIdx + variantIdxAddon),
                {
                    method: "GET",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => {
                    return response.json().then((data) => {
                        if (response.ok && typeof data.error == "undefined") {
                            resolve(data);
                        } else {
                            if (typeof data.error != "undefined" && data.msg.includes("not found")) {
                                resolve(data);
                            } else {
                                if (typeof data === 'object') {
                                    data.status = response.status;
                                }
                                reject(data);
                            }
                        }
                    }).catch(() => {
                        reject({status: response.status, error: "Invalid JSON response"});
                    });
                })
                .catch((err) => reject(err));
        });
    }

    get_order_data(sessionId: string) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST +
                "/session/" +
                country +
                "/" +
                language +
                "/" +
                sessionId.toLowerCase() +
                "/order",
                {
                    method: "GET",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    getPdf(sessionId: string) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase() + "/pdf",
                {
                    method: "GET",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    patch(sessionId: string, item: number, action: string, value: any) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase(),
                {
                    method: "PATCH",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        item: parseInt(String(item)),
                        action: action,
                        value: value,
                    }),
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    patchSet(sessionId: string, value: string, items: { itemIdx: number, valueIdx: string }[]) {
        const {country, language} = this.getLocaleParams();
        const payload: { item: number, action: string, value: string }[] = []
        for (const item of items) {
            payload.push({item: parseInt(String(item.itemIdx)), action: item.valueIdx, value: value})
        }

        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase(),
                {
                    method: "PATCH",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    // Like patchSet, but allows a distinct action/value pair per item instead of applying
    // the same value to every item (e.g. applying a stone preset to one item while forcing
    // "no stones" on the others).
    patchMultiple(sessionId: string, items: { itemIdx: number, action: string, value: string }[]) {
        const {country, language} = this.getLocaleParams();
        const payload: { item: number, action: string, value: string }[] = items.map((item) => ({
            item: parseInt(String(item.itemIdx)),
            action: item.action,
            value: item.value,
        }));

        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase(),
                {
                    method: "PATCH",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    deleteItem(sessionId: string, itemIdx: number) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase() + "/item/" + itemIdx,
                {
                    method: "DELETE",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    addItem(sessionId: string, itemIdx: number) {
        const {country, language} = this.getLocaleParams();
        return new Promise((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase() + "/item/" + itemIdx,
                {
                    method: "PUT",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }

    rubi(sessionId: string, itemIdx: number, message: string): Promise<sessionType> {
        const {country, language} = this.getLocaleParams();
        return new Promise<sessionType>((resolve, reject) => {
            fetch(
                process.env.VUE_APP_API_HOST + "/session/" + country + "/" + language + "/" + sessionId.toLowerCase() + "/rubi",
                {
                    method: "PUT",
                    mode: "cors",
                    cache: "no-cache",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        item_idx: itemIdx,
                        message: message
                    }),
                },
            )
                .then((response) => response.json())
                .then((data) => {
                    if (typeof data == "object" && typeof data.error == "undefined") {
                        resolve(data as sessionType);
                    } else {
                        reject(data);
                    }
                });
        });
    }
}
